// deadline-rate-limit-signal.ts — backlog C1-F4 (2026-10-06).
//
// A worker that hits a rate limit mid-session does not end: Claude Code keeps
// retrying internally and reports each wait as a stream event
// `{"type":"system","subtype":"api_retry","error_status":429,"error":"rate_limit",...}`
// (verified in the installed 2.1.283 binary: 429 ⇒ "rate_limit", and a
// dedicated emitter for persistent rate-limit waits). When the deadline kills
// such a session there is no result event, so error-classifier.ts never sees
// it and the attempt went down the timeout path instead of quota-sleep.
//
// Signal used: the LAST API-level event (assistant/user/result/api_retry)
// before the kill is a rate-limit retry, i.e. the worker was still waiting
// on it. A rate limit that was retried successfully (assistant output after
// it) and a hang for some other reason stays a plain timeout.
// Review 261006 M3a — freshness: in non-partial stream-json a long thinking
// block emits nothing until it completes, so "last event" alone could be a
// retry that succeeded minutes ago. It only counts when the kill came within
// the wait the event announced: killAt − arrival ≤ retry_delay_ms + 60 s.
// Read AFTER the group kill: the last line can sit in the stdout redactor's
// held-back tail until the child's `exit` flushes it; stdout bytes that
// arrive after `exit` are not seen (the fallback is then a plain timeout).
import type { ParsedEvent } from "./stream-json-event-parser.js";

const API_LEVEL_KINDS = new Set<ParsedEvent["kind"]>([
  "assistant",
  "user",
  "result",
  "system_api_retry",
]);

const FRESHNESS_SLACK_MS = 60_000;

export interface ObservedStream {
  events: ParsedEvent[];
  /** Parallel to `events` (session-runner-stream-consumer.ts). */
  eventReceivedAtMs: number[];
}

function wasWaitingOnRateLimitAtDeadline(stream: ObservedStream, killAtMs: number): boolean {
  for (let i = stream.events.length - 1; i >= 0; i -= 1) {
    const event = stream.events[i];
    if (event === undefined || !API_LEVEL_KINDS.has(event.kind)) continue;
    if (event.kind !== "system_api_retry") return false;
    const raw = event.raw as { error?: unknown; error_status?: unknown; retry_delay_ms?: unknown };
    if (raw.error !== "rate_limit" && raw.error_status !== 429) return false;
    const announcedWaitMs = typeof raw.retry_delay_ms === "number" ? raw.retry_delay_ms : 0;
    const receivedAtMs = stream.eventReceivedAtMs[i] ?? Number.NEGATIVE_INFINITY;
    return killAtMs - receivedAtMs <= announcedWaitMs + FRESHNESS_SLACK_MS;
  }
  return false;
}

/** `reason` (+ `category` when rate-limited) for a session killed at its deadline. */
export function deadlineReasonAndCategory(
  stream: ObservedStream,
  killAtMs: number,
): { reason: string; category?: "rate_limit" } {
  return wasWaitingOnRateLimitAtDeadline(stream, killAtMs)
    ? { reason: "rate_limit_retry_pending_at_deadline", category: "rate_limit" }
    : { reason: "deadline_ms_exceeded" };
}
